import { Skeleton } from 'primeng/skeleton';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Textarea } from 'primeng/textarea';
import {
  ACTIVE_CASE_STATUSES,
  isClosedCaseStatus,
  CARE_PLAN_CLOSED_REASON_LABEL,
  CARE_PLAN_CASE_STATUS_LABEL,
  CarePlanCase,
  CarePlanCaseStatus,
} from '../../care-plan.model';
import { CarePlanStore } from '../../care-plan.store';
import { CarePlanAnalytics } from '../../analytics/care-plan-analytics';
import { CARE_PLAN_DIALOG_OPTIONS } from '../../care-plan-dialog.options';
import { caseApiErrorMessage, caseApiErrorStatus } from '../api/case-api-error';
import { CaseGoalLinksComponent } from '../case-goal-links/case-goal-links.component';
import {
  CloseCaseDialogComponent,
  CloseCaseDialogData,
} from '../close-case-dialog/close-case-dialog.component';
import {
  ManageCaseGoalsDialogComponent,
  ManageCaseGoalsDialogData,
} from '../manage-case-goals-dialog/manage-case-goals-dialog.component';
import { OptionsSectionComponent } from '../../options/options-section/options-section.component';
import { ActionsSectionComponent } from '../../actions/actions-section/actions-section.component';

import { OutcomesSectionComponent } from '../../outcomes/outcomes-section/outcomes-section.component';

/** Case detail with Plan and Outcomes; editors use guarded dialogs. */
@Component({
  selector: 'bn-case-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Skeleton,
    FormsModule,
    OutcomesSectionComponent,
    CaseGoalLinksComponent,
    OptionsSectionComponent,
    ActionsSectionComponent,
    Textarea,
  ],
  providers: [DialogService],
  templateUrl: './case-detail.component.html',
  styleUrl: './case-detail.component.scss',
  host: {
    '(document:click)': 'closeStatusMenu()',
  },
})
export class CaseDetailComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _messages = inject(MessageService, { optional: true });

  readonly outcomesAvailable = computed(() => this.store().isLive());
  readonly selectedTab = signal<'plan' | 'outcomes'>('plan');
  readonly activeTab = computed(() =>
    this.outcomesAvailable() ? this.selectedTab() : 'plan',
  );

  onTabKeydown(event: KeyboardEvent): void {
    if (
      !this.outcomesAvailable() ||
      !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
    )
      return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 'plan'
        : event.key === 'End'
          ? 'outcomes'
          : this.activeTab() === 'plan'
            ? 'outcomes'
            : 'plan';
    this.selectedTab.set(next);
    const nav = (event.currentTarget as HTMLElement).closest(
      '[role="tablist"]',
    );
    nav?.querySelector<HTMLElement>(`[data-tab="${next}"]`)?.focus();
  }

  readonly caseId = input.required<string>();
  readonly store = input.required<CarePlanStore>();

  /** Asks the host row to collapse — emitted once the case is closed out. */
  readonly collapse = output<void>();

  protected readonly statusLabel = CARE_PLAN_CASE_STATUS_LABEL;

  protected readonly isClosedStatus = isClosedCaseStatus;

  readonly editingDescription = signal(false);
  readonly draftDescription = signal('');
  readonly descriptionSaving = signal(false);
  readonly descriptionError = signal<string | null>(null);
  readonly statusMenuOpen = signal(false);
  readonly statusSaving = signal(false);
  readonly caseSaving = computed(
    () =>
      this.descriptionSaving() ||
      this.statusSaving() ||
      this.store().isCaseSaving(this.caseId()),
  );
  private readonly _statusTrigger =
    viewChild<ElementRef<HTMLButtonElement>>('statusTrigger');
  private readonly _statusMenuItems =
    viewChildren<ElementRef<HTMLButtonElement>>('statusMenuItem');

  protected readonly caseRecord = computed(() =>
    this.store()
      .cases()
      .find((c) => c.id === this.caseId()),
  );

  protected readonly liveReadOnly = computed(
    () => this.store().isLive() && !this.store().canEditLiveCases(),
  );

  protected closedReasonLabel(record: CarePlanCase): string {
    if (record.closedReason) {
      return CARE_PLAN_CLOSED_REASON_LABEL[record.closedReason];
    }
    const suffix = record.closedReasonCode?.replace(/^CASE_CLOSED_REASON_/, '');
    if (!suffix) return 'Reason unavailable';
    return suffix
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /** Any other active status — not just the design's linear next-step. */
  protected readonly statusOptions = computed<
    { label: string; value: CarePlanCaseStatus }[]
  >(() => {
    const current = this.caseRecord()?.status;
    if (isClosedCaseStatus(current)) return [];
    if (this.store().isLive()) {
      if (this.store().caseStatusLoading() || this.store().caseStatusError())
        return [];
      return (this.store().caseStatusReference()?.CASE_STATUS ?? [])
        .filter(
          (item) =>
            item.code.toLowerCase() !== 'closed' &&
            item.code.toLowerCase() !== 'suggested' &&
            item.code.toLowerCase() !== current,
        )
        .map((item) => ({
          label: item.value,
          value: item.code.toLowerCase(),
        }));
    }
    return ACTIVE_CASE_STATUSES.filter((s) => s !== current).map((s) => ({
      label: CARE_PLAN_CASE_STATUS_LABEL[s],
      value: s,
    }));
  });

  protected readonly canClose = computed(
    () =>
      !this.store().isLive() ||
      (!this.store().caseStatusLoading() &&
        !this.store().caseStatusError() &&
        !!this.store()
          .caseStatusReference()
          ?.CASE_STATUS.some((item) => item.code.toLowerCase() === 'closed')),
  );

  startEditDescription(): void {
    if (
      this.caseSaving() ||
      this.liveReadOnly() ||
      isClosedCaseStatus(this.caseRecord()?.status)
    )
      return;
    this.draftDescription.set(this.caseRecord()?.description ?? '');
    this.descriptionError.set(null);
    this.editingDescription.set(true);
  }

  cancelDescription(): void {
    if (this.caseSaving()) return;
    this.editingDescription.set(false);
    this.descriptionError.set(null);
    this.draftDescription.set('');
  }

  async saveDescription(): Promise<void> {
    if (
      this.caseSaving() ||
      this.liveReadOnly() ||
      isClosedCaseStatus(this.caseRecord()?.status)
    )
      return;
    const value = this.draftDescription().trim();
    if (!value || value.length > 250) {
      this.descriptionError.set(
        'Enter a description between 1 and 250 characters.',
      );
      return;
    }
    this.descriptionSaving.set(true);
    this.descriptionError.set(null);
    try {
      if (this.store().isLive()) {
        await this.store().updateLiveCaseDescription(this.caseId(), value);
      } else {
        this.store().updateCaseDescription(this.caseId(), value);
      }
      this.editingDescription.set(false);
    } catch (error) {
      this.descriptionError.set(
        caseApiErrorMessage(
          error,
          'The description could not be saved. Try again.',
        ),
      );
    } finally {
      this.descriptionSaving.set(false);
    }
  }

  toggleStatusMenu(event: Event): void {
    if (
      this.liveReadOnly() ||
      this.caseSaving() ||
      isClosedCaseStatus(this.caseRecord()?.status)
    )
      return;
    event.stopPropagation();
    const opening = !this.statusMenuOpen();
    this.statusMenuOpen.set(opening);
    if (opening) {
      setTimeout(() => this._focusStatusMenuItem(0));
    }
  }

  closeStatusMenu(restoreFocus = false): void {
    if (this.statusMenuOpen()) {
      this.statusMenuOpen.set(false);
      if (restoreFocus) {
        setTimeout(() => this._statusTrigger()?.nativeElement.focus());
      }
    }
  }

  onStatusMenuKeydown(event: KeyboardEvent, index: number): void {
    const itemCount = this._statusMenuItems().length;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeStatusMenu(true);
      return;
    }
    if (itemCount === 0) return;

    let nextIndex: number | null = null;
    if (event.key === 'ArrowDown') nextIndex = (index + 1) % itemCount;
    if (event.key === 'ArrowUp')
      nextIndex = (index - 1 + itemCount) % itemCount;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = itemCount - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    this._focusStatusMenuItem(nextIndex);
  }

  async onStatusChange(status: CarePlanCaseStatus): Promise<void> {
    if (
      this.liveReadOnly() ||
      this.caseSaving() ||
      isClosedCaseStatus(this.caseRecord()?.status)
    )
      return;
    this.statusMenuOpen.set(false);
    const fromStatus = this.caseRecord()?.status;
    if (!fromStatus) return;
    if (
      this.store().isLive() &&
      !this.statusOptions().some((option) => option.value === status)
    )
      return;
    this.statusSaving.set(true);
    try {
      if (this.store().isLive()) {
        await this.store().transitionLiveCaseStatus(this.caseId(), status);
      } else {
        this.store().updateCaseStatus(this.caseId(), status);
      }
      this._analytics.caseStatusChanged(this.caseId(), fromStatus, status);
    } catch (error) {
      this._showStatusError(error);
    } finally {
      this.statusSaving.set(false);
    }
  }

  async reopen(): Promise<void> {
    if (this.liveReadOnly() || this.caseSaving()) return;
    const fromStatus = this.caseRecord()?.status;
    if (!fromStatus) return;
    const priorStatus = this.caseRecord()?.priorStatus;
    const toStatus =
      priorStatus && !isClosedCaseStatus(priorStatus)
        ? priorStatus
        : 'in_progress';
    this.statusSaving.set(true);
    try {
      if (this.store().isLive()) {
        await this.store().transitionLiveCaseStatus(this.caseId(), toStatus);
      } else {
        this.store().reopenCase(this.caseId());
      }
      this._analytics.caseStatusChanged(this.caseId(), fromStatus, toStatus);
    } catch (error) {
      this._showStatusError(error);
    } finally {
      this.statusSaving.set(false);
    }
  }

  openCloseDialog(): void {
    if (this.liveReadOnly() || this.caseSaving() || !this.canClose()) return;
    this.statusMenuOpen.set(false);
    const ref = this._dialog.open(CloseCaseDialogComponent, {
      ...CARE_PLAN_DIALOG_OPTIONS,
      styleClass: 'bn-close-case-dialog-host',
      data: {
        store: this.store(),
        caseId: this.caseId(),
      } satisfies CloseCaseDialogData,
    });
    ref?.onClose.subscribe(() => {
      if (isClosedCaseStatus(this.caseRecord()?.status)) {
        this.collapse.emit();
      }
    });
  }

  openManageGoals(): void {
    if (!this.store().canManageGoals()) return;
    this._dialog.open(ManageCaseGoalsDialogComponent, {
      ...CARE_PLAN_DIALOG_OPTIONS,
      styleClass: 'bn-manage-case-goals-dialog-host',
      data: {
        store: this.store(),
        caseId: this.caseId(),
      } satisfies ManageCaseGoalsDialogData,
    });
  }

  private _focusStatusMenuItem(index: number): void {
    this._statusMenuItems()[index]?.nativeElement.focus();
  }

  private _showStatusError(error: unknown): void {
    // ApiService already emits the transport-level HTTP 500 toast.
    if (caseApiErrorStatus(error) === 500) return;
    this._messages?.add({
      severity: 'error',
      summary: "Couldn't change case status",
      detail: caseApiErrorMessage(
        error,
        'The case status could not be changed. Try again.',
      ),
    });
  }
}
